import { useSearchParams } from "react-router-dom";
import { Btn, Card, Notice } from "../components/ui";
import config from "../config";
import logo from "../logos/sidekick.svg";
import { colors, fonts } from "../style";

const style = {
  page: {
    position: "relative",
    overflow: "hidden",
    minHeight: "100vh",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "20px",
    padding: "48px 16px",
    background: colors.pageBackground,
    fontFamily: fonts.system,
    color: colors.textStrong,
  },
  // Faint brand glow behind the card
  glow: {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "900px",
    height: "900px",
    transform: "translate(-50%, -55%)",
    borderRadius: "50%",
    pointerEvents: "none",
    background:
      "radial-gradient(closest-side, rgba(125, 134, 224, 0.16), rgba(47, 167, 162, 0.06) 60%, transparent)",
  },
  card: {
    position: "relative",
    width: "100%",
    maxWidth: "360px",
    boxSizing: "border-box",
    padding: "36px 32px 32px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  provider: { width: "100%", height: "46px", gap: "10px", fontSize: "15px" },
  title: {
    margin: "20px 0 6px",
    fontSize: "20px",
    fontWeight: 700,
    color: colors.text,
  },
  subtitle: { margin: "0 0 24px", fontSize: "14px", color: colors.textMuted },
  buttons: { width: "100%", display: "grid", gap: "12px" },
  legal: {
    position: "relative",
    margin: 0,
    fontSize: "13px",
    color: colors.textLight,
  },
};

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}

function Login() {
  const [searchParams] = useSearchParams();
  const err = searchParams.get("err");
  // The "next" build only signs in with Microsoft
  const googleLogin = config.customer !== "next";

  const getMsLoginUrl = () => {
    const url = `${config.api_url}/login/microsoft?spa_app_url=${config.app_url}&product=${config.product}`;

    if (config.customer === "next") {
      return url + "&type=next";
    }

    return url;
  };

  const getGoogleLoginUrl = () =>
    `${config.api_url}/login/google?spa_app_url=${config.app_url}&product=${config.product}`;

  return (
    <div style={style.page}>
      <div style={style.glow} />
      <Card style={style.card}>
        <img src={logo} alt="" height="64" />
        <h1 style={style.title}>Sign in to Sidekick</h1>
        <p style={style.subtitle}>
          {googleLogin
            ? "Use your work Microsoft or Google account."
            : "Use your work Microsoft account."}
        </p>
        <div style={style.buttons}>
          <Btn as="a" href={getMsLoginUrl()} size="lg" style={style.provider}>
            <MicrosoftLogo />
            Sign in with Microsoft
          </Btn>
          {googleLogin && (
            <Btn
              as="a"
              href={getGoogleLoginUrl()}
              size="lg"
              style={style.provider}
            >
              <GoogleLogo />
              Sign in with Google
            </Btn>
          )}
        </div>
        {err === "ulExceeded" && (
          <Notice tone="error" style={{ marginTop: "20px", textAlign: "left" }}>
            Your organisation has no user licences left. Ask your administrator
            to add one.
          </Notice>
        )}
      </Card>
      <p style={style.legal}>
        <a href={config.privacy_policy_url} target="_blank" rel="noreferrer">
          Privacy Policy
        </a>
      </p>
    </div>
  );
}

export default Login;
